import { Fragment } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

/** A rule at every group boundary, so the eye finds it without counting. */
export const GROUP_EDGE = "border-l border-solid border-collectors-black";

/**
 * A grouped-column table whose rows open into child rows.
 *
 * Extracted from the weekly performance table when the product table wanted the
 * same thing. What is shared is the shape — two header rows, a dividing rule at
 * each group boundary, a clickable parent row that inverts when open, and the
 * loading/error/empty states each expansion can be in. What is *not* shared is
 * what any cell says: that comes back through `renderCell`, so each table keeps
 * its own formatting, colour rules and column definitions.
 *
 * @param {object[]} groups - column groups, already filtered for visibility:
 *        `{ key, label, columns: [{ key, label, align }] }`
 * @param {object[]} rows - parent rows
 * @param {(row) => string} rowKey - stable identity, also the expansion key
 * @param {(col, row, state) => React.ReactNode} renderCell - `state` carries
 *        `{ open, isChild }` so a cell can render differently in a child row
 * @param {(row, state) => string} [rowClassName] - extra classes per cell
 * @param {object} expansion - `{ open: Set, children: {}, loading: Set,
 *        errors: {}, onToggle }`
 * @param {string} [emptyLabel]
 * @param {string} [childEmptyLabel]
 */
export default function ExpandableTable({
  groups,
  rows,
  rowKey,
  renderCell,
  rowClassName = () => "",
  expansion,
  emptyLabel = "Nothing in this time range.",
  childEmptyLabel = "No detail for this row.",
}) {
  if (!rows || rows.length === 0) {
    return (
      <p className="font-mono text-xs uppercase tracking-widest text-collectors-gray py-6">
        {emptyLabel}
      </p>
    );
  }

  const { open, children, loading, errors, onToggle } = expansion;
  const flat = groups.flatMap((g) =>
    g.columns.map((c, i) => ({ ...c, groupKey: g.key, firstInGroup: i === 0 }))
  );

  function messageRow(text, tone = "text-collectors-gray") {
    return (
      <tr className="border-b border-solid border-collectors-light">
        <td
          colSpan={flat.length}
          className={`px-3 py-3 pl-10 font-mono text-[11px] uppercase tracking-widest ${tone}`}
        >
          {text}
        </td>
      </tr>
    );
  }

  function cells(row, state) {
    return flat.map((col) => (
      <td
        key={col.key}
        className={`px-3 ${state.isChild ? "py-1.5 text-[11px]" : "py-2 text-xs"} font-mono
          ${col.wrap ? "" : "whitespace-nowrap"}
          ${col.align === "left" ? "text-left" : "text-right"}
          ${col.firstInGroup ? GROUP_EDGE : ""}
          ${rowClassName(row, { ...state, col })}`}
      >
        {renderCell(col, row, state)}
      </td>
    ));
  }

  return (
    <div className="overflow-x-auto border border-solid border-collectors-black">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-solid border-collectors-light">
            {groups.map((g) => (
              <th
                key={g.key}
                colSpan={g.columns.length}
                className={`px-3 py-1.5 text-left font-mono text-[10px] uppercase tracking-[0.2em]
                  text-collectors-gray ${g.key === groups[0].key ? "" : GROUP_EDGE}`}
              >
                {g.label}
              </th>
            ))}
          </tr>
          <tr className="border-b border-solid border-collectors-black">
            {flat.map((col) => (
              <th
                key={col.key}
                title={col.title}
                className={`px-3 py-2 font-mono text-[11px] uppercase tracking-wider whitespace-nowrap
                  ${col.align === "left" ? "text-left" : "text-right"}
                  ${col.firstInGroup ? GROUP_EDGE : ""}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            // Any number of rows can be open at once — comparing two or three
            // side by side is most of the point of breaking them out.
            const key = rowKey(row);
            const isOpen = open.has(key);
            const isLoading = loading.has(key);
            const error = errors[key];
            const childRows = children[key];

            return (
              <Fragment key={key}>
                <tr
                  onClick={() => onToggle(key, row)}
                  className={`border-b border-solid cursor-pointer transition-colors ${
                    isOpen
                      ? "bg-collectors-black text-white border-collectors-black"
                      : "border-collectors-light hover:bg-collectors-light/40"
                  }`}
                >
                  {cells(row, { open: isOpen, isChild: false })}
                </tr>

                {isOpen && isLoading && messageRow("Loading…")}
                {isOpen && error && messageRow(error, "text-red-700")}
                {isOpen &&
                  !isLoading &&
                  !error &&
                  childRows &&
                  childRows.length === 0 &&
                  messageRow(childEmptyLabel)}
                {isOpen &&
                  !isLoading &&
                  !error &&
                  childRows &&
                  childRows.map((child, i) => (
                    <tr
                      key={`${key}-${i}`}
                      className="border-b border-solid border-collectors-light bg-collectors-light/30"
                    >
                      {cells(child, { open: false, isChild: true })}
                    </tr>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** The open/closed chevron every expandable first column uses. */
export function ExpandChevron({ open }) {
  return open ? <ChevronDown size={14} /> : <ChevronRight size={14} />;
}
